import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { message } from "antd";
import dayjsTZ from "../../../utils/dayjs";
import AgGridComponent from "../../AG-grid/AgGridComponent";
import PunonjesitContext from "../store/PunonjesitContext";
import getAgThemePreference from "../../../utils/getAgThemePreference";
import { colDefs } from "./colDefs";
import { requestDataWithParams } from "../../../helpers/API/RequestFactory";
import { FetchIcon } from "../../../assets/icons";
import RangeFilterModal from "../components/RangeFilterModal";

const CLOCKIN_TYPES_FIELD_ID = "e685cd30-b119-11ec-829e-112b583587c7";

const { accentColor, foregroundColor } = getAgThemePreference();

const ClockIns = () => {
  const { employee } = useContext(PunonjesitContext);

  const [{ clockings }, { isDarkMode }, { programFields }] = useSelector(
    (state) => [state.clockings, state.usersList, state.programFields]
  );

  const [rowData, setRowData] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [activeFilter, setActiveFilter] = useState([]);
  const [fetchModalOpen, setFetchModalOpen] = useState(false);

  //region currentMonthClockings
  const currentMonthClockings = useMemo(() => {
    if (!clockings || !employee?.employeeId) return [];
    return clockings.filter(
      (el) =>
        el.employeeId === employee?.employeeId &&
        el.clockInDate >= dayjsTZ().startOf("month").valueOf() &&
        el.clockInDate <= dayjsTZ().endOf("month").valueOf()
    );
  }, [clockings, employee?.employeeId]);

  const dayTypes = useMemo(() => {
    if (!programFields) return {};
    const idx = programFields.findIndex(
      (el) => el?.fieldId === CLOCKIN_TYPES_FIELD_ID
    );
    if (idx === -1) return {};
    const toReturn = {};

    (programFields[idx].fieldOptions || []).forEach((el = {}) => {
      toReturn[el.name] = el.description;
    });

    return toReturn;
  }, [programFields]);

  const columnDefs = colDefs({ dayTypes });

  //region FETCH RANGE
  const fetchRange = async (initialData) => {
    let data = [...(initialData || [])];

    if (!initialData) {
      message.loading({
        key: "fetching",
        content: "Duke ngarkuar të dhënat...",
      });
      const filters = activeFilter.filter(Boolean);
      if (filters.length !== 2) {
        message.error({
          content: "Ju lutem zgjidhni një periudhë të vlefshme!",
          key: "fetching",
          duration: 1.5,
        });
        return;
      }
      message.loading({
        key: "fetching",
        content: "Duke ngarkuar të dhënat...",
      });
      const [start, end] = filters;
      data = await requestDataWithParams(
        "clocking",
        dayjsTZ(start).valueOf(),
        dayjsTZ(end).add(1, "day").valueOf(),
        { employeeId: employee?.employeeId }
      );
    }

    const sorted = (data || [])
      .map((el) => ({
        ...el,
        fillim_turni: el?.clockInDate,
        mbarim_turni: el?.clockOutDate,
        duration: !!el?.clockOutDate
          ? dayjsTZ(el?.clockOutDate)
              .diff(dayjsTZ(el?.clockInDate), "hours", true)
              .toFixed(2)
          : 0,
      }))
      .sort((a, b) => b?.clockInDate - a?.clockInDate);

    setRowData(sorted);
    setFetchModalOpen(false);

    !initialData &&
      message.success({
        key: "fetching",
        content: "Të dhënat u përditësuan me sukses!",
        duration: 1.5,
      });
  };

  const handleClean = () => {
    setActiveFilter([]);
    fetchRange(currentMonthClockings);
  };

  useEffect(() => {
    message.loading({ key: "fetching", content: "Duke ngarkuar të dhënat..." });
    if (!!employee && Array.isArray(clockings)) {
      fetchRange(currentMonthClockings);
    }
  }, [clockings, employee]);

  const EXPORT_COLS = [
    {
      en: "dayType",
      al: "Lloji i turnit",
      replace: dayTypes,
    },
    {
      en: "approved",
      al: "Statusi",
      replace: {
        true: "I aprovuar",
        false: "I pa aprovuar",
      },
    },
    { en: "duration", al: "Kohëzgjatja (orë)" },
  ];

  const ITEMS = {
    search: { handleClean },
    icons: {
      excel: { tableCols: EXPORT_COLS },
      pdf: { tableCols: EXPORT_COLS },
      print: { tableCols: EXPORT_COLS },
      modals: [
        {
          icon: (
            <FetchIcon
              width={20}
              height={20}
              fill={isDarkMode ? foregroundColor : accentColor}
            />
          ),
          title: "Filtro në muajt e mëparshëm",
          onClick: () => setFetchModalOpen(true),
        },
      ],
    },
  };

  //region RETURN
  return (
    <div className="globalPageWrapper">
      <AgGridComponent
        gridApi={gridApi}
        rowData={rowData}
        rowSelection={{ mode: "multiRow" }}
        onGridReady={(p) => setGridApi(p.api)}
        columnDefs={columnDefs}
        idKey={"clockingId"}
        headerProps={{
          items: ITEMS,
          exportTitle: `Oraret - ${employee?.employeeFirstName} ${employee?.employeeLastName}`,
        }}
        counterProps={{
          className: "rowFlex",
          title: "Totali i orareve (Aprovuar)",
          data: rowData?.map((el) =>
            !!el.clockOutDate
              ? dayjsTZ(el.clockOutDate)
                  .diff(dayjsTZ(el.clockInDate), "hours", true)
                  .toFixed(2)
              : 0
          ),
          derivedData: rowData?.map((el) =>
            !!el.clockOutDate && !!el?.approved
              ? dayjsTZ(el.clockOutDate)
                  .diff(dayjsTZ(el.clockInDate), "hours", true)
                  .toFixed(2)
              : 0
          ),
        }}
      />
      {fetchModalOpen ? (
        <RangeFilterModal
          open={fetchModalOpen}
          setOpen={setFetchModalOpen}
          activeFilter={activeFilter}
          onFetch={fetchRange}
          setActiveFilter={setActiveFilter}
        />
      ) : null}
    </div>
  );
};

export default ClockIns;
